import { Box } from '@mantine/core'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { BoggleGameState } from '~/boggle/engine/standard/types'
import { BOGGLE_GOLD_RUSH_BURST_ID } from '~/boggle/lib/meterBursts'
import { useSyncBoggleRouteShellAmbientAnimationPaused } from '~/boggle/ui/chrome/RouteShell'
import { useBoggleRunShellAmbientSync } from '~/boggle/ui/chrome/useBoggleRunShellAmbientSync'
import {
  ScreenFeedbackPulseLayer,
  type BoggleScoreFeedbackState,
} from '~/boggle/ui/run/ScoreFeedback'
import {
  useBogglePlayingPhasePaused,
  type BoggleStore,
} from '~/boggle/ui/state/Store'

/**
 * Ambient + score-pulse scaffolding shared by every run variant
 * (`StandardRunSurface`, `ChallengeRunSurface`, …). The visual surface itself
 * (top chrome, HUD, board, summary) lives in `RunSurface`.
 */
export function RunVariantShell({
  children,
  scoreFeedback,
  state,
  store,
  summaryStandardOutcome = null,
}: {
  children: ReactNode
  scoreFeedback: BoggleScoreFeedbackState
  state: BoggleGameState
  store: BoggleStore
  /** Pass `'new-best' | 'tie'` from standard variants so ambient mood reacts to the outcome. */
  summaryStandardOutcome?: null | 'new-best' | 'tie'
}) {
  const runAmbientPaused = useBogglePlayingPhasePaused(store, state.phase)
  useSyncBoggleRouteShellAmbientAnimationPaused(runAmbientPaused)
  useBoggleRunShellAmbientSync(store, { summaryStandardOutcome })

  const [goldRushPulseKey, setGoldRushPulseKey] = useState(0)
  const wasGoldRushActiveRef = useRef(
    state.meter.burstActive && state.meter.activeBurstId === BOGGLE_GOLD_RUSH_BURST_ID,
  )

  useEffect(() => {
    return () => {
      store.resume()
    }
  }, [store])

  useEffect(() => {
    const goldRushActive =
      state.meter.burstActive && state.meter.activeBurstId === 'gold-rush'
    if (goldRushActive && !wasGoldRushActiveRef.current) {
      setGoldRushPulseKey(current => current + 1)
    }
    wasGoldRushActiveRef.current = goldRushActive
  }, [state.meter.activeBurstId, state.meter.burstActive])

  return (
    <Box h="100%" mih={0} pos="relative">
      <ScreenFeedbackPulseLayer
        goldRushPulseKey={goldRushPulseKey}
        scorePulse={scoreFeedback.boardPulse}
      />
      {children}
    </Box>
  )
}
